import {
  CategoryScale,
  Chart as ChartJS,
  type ChartOptions,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { Divider } from 'antd';
import Modal from 'antd/es/modal/Modal';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { HealthMetricType, Measurement } from '../../fetchHooks/useHealthmetrictypesWithMeasurements';
import { formatDateOnly } from '../../utils/utilFunctions.tsx';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, TimeScale);

/**
 * Convert value from primary unit to alternative unit using conversion_rate from database
 */
function convertToAlternativeUnit(value: number, healthMetric: HealthMetricType | undefined): number {
  if (!healthMetric?.conversion_rate || !healthMetric?.unit_alternative) return 0;
  const convertedValue = value * healthMetric.conversion_rate;
  return Number(convertedValue.toFixed(1));
}

/**
 * Convert value from primary unit to alternative unit with unit label
 */
function convertToAlternativeUnitWithLabel(value: number, healthMetric: HealthMetricType | undefined): string {
  if (!healthMetric?.conversion_rate || !healthMetric?.unit_alternative) return 'N/A';
  const convertedValue = value * healthMetric.conversion_rate;
  return `${convertedValue.toFixed(1)} ${healthMetric.unit_alternative}`;
}

/**
 * Check if health metric has alternative unit conversion available
 */
function hasAlternativeUnit(healthMetric: HealthMetricType | undefined): boolean {
  return !!(healthMetric?.conversion_rate && healthMetric?.unit_alternative);
}

type MydayStatsGraphProps = {
  healthMetric: HealthMetricType | undefined;
  showBloodworkDescription?: boolean;
};

export const MydayStatsGraph: React.FC<MydayStatsGraphProps> = ({
  healthMetric,
  showBloodworkDescription,
}: MydayStatsGraphProps) => {
  const { i18n } = useTranslation();
  const [showModal, setShowModal] = React.useState(false);
  const navigate = useNavigate();
  if (!healthMetric?.id) return <></>;

  const handleShowMydayDetails = (mydayid: number) => {
    console.log('navitation to: ', `/mydaydetails/${mydayid}`);
    navigate(`/mydaydetails/${mydayid}`);
  };

  const hasAltUnit = hasAlternativeUnit(healthMetric);
  const measurements =
    healthMetric?.measurements?.sort((a, b) => new Date(a.myday?.date).getTime() - new Date(b.myday?.date).getTime()) ||
    [];
  const maxPrimary = Math.max(
    ...measurements.map((item) => item.value),
    healthMetric.ref_upper ?? Number.NEGATIVE_INFINITY,
  );
  const maxAlternative = convertToAlternativeUnit(maxPrimary, healthMetric);
  const minPrimary = Math.min(
    ...measurements.map((item) => item.value),
    healthMetric.ref_lower ?? Number.NEGATIVE_INFINITY,
  );

  const minAlternative = convertToAlternativeUnit(minPrimary, healthMetric);

  const options = {
    responsive: true,
    scales: {
      x: {
        parsing: false,
        type: 'time',
        time: {
          unit: 'day',
        },
        ticks: {
          major: {
            enabled: true,
          },
          // color: '#FF0000'
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        min: minPrimary * 0.95, // Set the minimum value for primary unit
        max: maxPrimary * 1.05, // Set the maximum value for primary unit
        title: {
          display: true,
          text: healthMetric?.unit,
        },
      },
      y1: {
        type: 'linear',
        display: hasAltUnit && !Number.isNaN(convertToAlternativeUnit(measurements?.[0]?.value, healthMetric)),
        position: 'right',
        min: minAlternative * 0.95, // Set the minimum value for alternative unit
        max: maxAlternative * 1.05, // Set the maximum value
        grid: {
          drawOnChartArea: false,
        },
        title: {
          display: true,
          text: healthMetric?.unit_alternative || '',
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
      },
    },
  } as ChartOptions<'line'>;

  const data = (compact = false) => {
    const tension = 0.1;
    const retVal = {
      labels: measurements?.map(({ myday }) => myday?.date).filter(Boolean),
      datasets: [
        {
          label: healthMetric?.unit || 'Value',
          data: measurements?.map((item) => item.value),
          borderColor: 'blue',
          backgroundColor: 'blue',
          tension,
          borderWidth: compact ? 1 : 2,
        },
      ],
    };

    // Add alternative unit dataset if available
    if (hasAltUnit && !Number.isNaN(convertToAlternativeUnit(measurements?.[0]?.value, healthMetric))) {
      retVal?.datasets?.push({
        label: healthMetric?.unit_alternative || 'Alternative',
        data: measurements?.map((item) => convertToAlternativeUnit(item.value, healthMetric)),
        borderColor: 'green',
        backgroundColor: 'green',
        tension,
        borderWidth: compact ? 1 : 2,
        // Note: yAxisID would be set via Chart.js options, not in dataset
      } as any); // Type assertion needed for Chart.js dataset with yAxisID
    }

    if (healthMetric.ref_lower)
      retVal?.datasets?.push({
        label: 'Ref Low',
        data: measurements?.map(() => healthMetric.ref_lower) as number[],
        borderColor: 'pink',
        backgroundColor: 'pink',
        tension,
        borderWidth: compact ? 1 : 2,
      });
    if (healthMetric.ref_upper)
      retVal?.datasets?.push({
        label: 'Ref High',
        data: measurements?.map(() => healthMetric.ref_upper) as number[],
        borderColor: 'orange',
        backgroundColor: 'orange',
        tension,
        borderWidth: compact ? 1 : 2,
      });
    return retVal;
  };

  return (
    <div>
      <h3 style={{ textAlign: 'center' }}>
        {i18n.language === 'en' && healthMetric?.description_en
          ? healthMetric.description_en
          : healthMetric?.description}
        :
      </h3>
      <Line options={options} data={data(true)} onClick={() => setShowModal((prev) => !prev)} />
      {showBloodworkDescription && <p style={{ whiteSpace: 'pre-wrap' }}>{healthMetric?.comment}</p>}
      <Divider />
      <Modal
        open={showModal}
        onCancel={() => setShowModal(false)}
        styles={{ body: { padding: 0, borderRadius: '20px' } }}
        width="1080px"
        footer={null}
        centered
        destroyOnHidden
      >
        {showModal && (
          <div>
            <h3 style={{ textAlign: 'center' }}>
              All{' '}
              {i18n.language === 'en' && healthMetric?.description_en
                ? healthMetric.description_en
                : healthMetric?.description}{' '}
              measurements:
            </h3>
            <TableWithTwoRows
              measurements={measurements}
              onClick={handleShowMydayDetails}
              healthMetric={healthMetric}
              hasAltUnit={hasAltUnit}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

type TableWithTwoRowsProps = {
  measurements: Measurement[];
  onClick?: (id: number) => void;
  healthMetric: HealthMetricType;
  hasAltUnit?: boolean;
};

const TableWithTwoRows: React.FC<TableWithTwoRowsProps> = ({
  measurements,
  onClick,
  healthMetric,
  hasAltUnit = false,
}) => {
  const { i18n } = useTranslation();
  if (!measurements) return <></>;
  const tableRows = measurements.map((item, index) => (
    <tr
      key={index}
      onClick={() => onClick?.(item.myday?.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(item.myday?.id);
        }
      }}
      tabIndex={0}
      aria-label={`View details for measurement on ${formatDateOnly(new Date(item.myday?.date), i18n.language)}`}
      style={{ cursor: 'pointer' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#f5f5f5';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '';
      }}
    >
      <td>{index + 1}</td>
      <td>{formatDateOnly(new Date(item.myday?.date), i18n.language)}</td>
      <td>
        {item.value} {healthMetric.unit}{' '}
        {hasAltUnit &&
          !Number.isNaN(convertToAlternativeUnit(item.value, healthMetric)) &&
          `(${convertToAlternativeUnitWithLabel(item.value, healthMetric)})`}
      </td>
    </tr>
  ));

  return (
    <table className="custom-table">
      <thead>
        <tr>
          <th>No.</th>
          <th>Date</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>{tableRows}</tbody>
    </table>
  );
};
